// src/utils/dietary.js
// Client-side dietary preference filtering. MenuItem.dietary stays a JSON-array string.

export function parseDietary(dietaryField) {
  if (!dietaryField) return [];
  if (Array.isArray(dietaryField)) return dietaryField.map(d => String(d).toLowerCase());
  try {
    const v = JSON.parse(dietaryField);
    return Array.isArray(v) ? v.map(d => String(d).toLowerCase()) : [];
  } catch { return []; }
}

export const DIETARY_RULES = {
  vegetarian: item => { const d = parseDietary(item.dietary); return d.includes('vegetarian') || d.includes('vegan'); },
  halal:      item => parseDietary(item.dietary).includes('halal'),
  vegan:      item => parseDietary(item.dietary).includes('vegan'),
  pescatarian: item => {
    const d = parseDietary(item.dietary);
    return d.includes('vegetarian') || d.includes('vegan') || d.includes('pescatarian') || d.includes('seafood');
  },
  none: () => true,
};

export const DIETARY_OPTIONS = [
  { key: 'vegetarian',  icon: '🌱', en: 'Vegetarian',    ar: 'نباتي',          descEn: 'No meat or fish',                 descAr: 'بدون لحوم أو أسماك' },
  { key: 'halal',       icon: '🥩', en: 'Halal',         ar: 'حلال',           descEn: 'Halal certified items only',      descAr: 'منتجات حلال فقط' },
  { key: 'vegan',       icon: '🌿', en: 'Vegan',         ar: 'نباتي صرف',      descEn: 'No animal products',              descAr: 'بدون منتجات حيوانية' },
  { key: 'pescatarian', icon: '🐟', en: 'Pescatarian',   ar: 'نباتي مع أسماك', descEn: 'Fish and seafood only, no meat',  descAr: 'أسماك ومأكولات بحرية بدون لحوم' },
  { key: 'none',        icon: '⚪', en: 'No preference', ar: 'بدون تفضيل',     descEn: 'Show me everything',              descAr: 'اعرض كل شيء' },
];

export const dietaryOption = (key) => DIETARY_OPTIONS.find(o => o.key === key) || DIETARY_OPTIONS[4];

// Splits a menu into what to show for the active preference.
//   visible    – items matching the preference
//   unverified – items with no dietary tags at all (excluded: we can't confirm them)
// When showUnverified is true they are appended, flagged `unverified: true`.
export function applyDietary(items, preference, showUnverified = false) {
  const list = items || [];
  if (!preference || preference === 'none') return { visible: list, unverified: [], shown: list };
  const rule = DIETARY_RULES[preference] || DIETARY_RULES.none;
  const visible = [], unverified = [];
  for (const it of list) {
    if (parseDietary(it.dietary).length === 0) unverified.push(it);
    else if (rule(it)) visible.push(it);
  }
  const shown = showUnverified ? [...visible, ...unverified.map(i => ({ ...i, unverified: true }))] : visible;
  return { visible, unverified, shown };
}

// Category tabs only for categories that still have a visible item.
export function categoriesOf(items) {
  return ['All', ...new Set((items || []).map(i => i.category).filter(Boolean))];
}

// Vendor-admin tag lists (stored lowercase slugs in MenuItem.dietary / allergens)
export const DIETARY_TAGS = [
  ['vegetarian', 'Vegetarian'], ['vegan', 'Vegan'], ['halal', 'Halal'], ['pescatarian', 'Pescatarian'],
  ['contains-gluten', 'Contains Gluten'], ['contains-dairy', 'Contains Dairy'],
  ['contains-nuts', 'Contains Nuts'], ['contains-egg', 'Contains Egg'],
];
export const ALLERGEN_TAGS = [
  ['gluten', 'Gluten'], ['dairy', 'Dairy'], ['nuts', 'Nuts'], ['egg', 'Egg'],
  ['soy', 'Soy'], ['fish', 'Fish'], ['shellfish', 'Shellfish'], ['sesame', 'Sesame'],
];
